//
// 03/05/2019 note:
// When changing any site settings, be sure to follow the process in the "Settings Support Work In-take Process" wiki page:
//    https://mytableau.tableaucorp.com/x/coDWD
//

import * as _ from 'underscore';

import { appStore } from '../../app/AppStore';
import { NavLocationSelectors } from '../../app/selectors/NavigationStateSelectors';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { Location } from '../../common/react/Location';
import { SiteSettingServerAdminTelemetryEvent } from '../../common/telemetryEvents/TelemetryEvents';
import { buildSiteSettingServerAdminTelemetryEvent, getSiteSettingUUID } from '../../common/telemetryEvents/util/SiteSettingTelemetryUtil';
import { NumberInputRange } from '../../ts/directives/tbRangeInputValidator';
import { ByteSizeConversionFactor } from '../../ts/filters/SizeFilter';
import { L10n } from '../../ts/L10n/L10n';
import { Module } from '../../ts/Module';
import { SiteAvailabilityState, Sites } from '../../ts/resources/Sites';
import { GetNumExcessRevisionsRequest, IGetNumExcessRevisionsParams, IGetNumExcessRevisionsResult } from '../../ts/ServerApi/requests/GetNumExcessRevisionsRequest';
import { GetNumManagedKeychainsRequest, IGetNumManagedKeychainParams, IGetNumManagedKeychainResult } from '../../ts/ServerApi/requests/GetNumManagedKeychainRequest';
import { IError } from '../../ts/ServerApi/types/IError';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { ClearOrphanedManagedKeychainAction } from '../../ts/services/ClearOrphanedManagedKeychainAction';
import { ClearSavedRevisionsAction } from '../../ts/services/ClearSavedRevisionsAction';
import { ILicensingInfoWithTotals, LicensesService } from '../../ts/services/LicensesService';
import { ServerService } from '../../ts/services/ServerService';
import { SiteSwitchService } from '../../ts/services/SiteSwitchService';
import { ISettingsCoordinatorCtrlScope, SettingsFormService } from '../directives/tbSettingsCoordinator';
import { ISiteSettingsForServerAdmin } from '../types/ISite';
import { ISiteSettingsForSiteAdmin, UserQuotaMode } from '../types/ISiteSettingsForSiteAdmin';
import { HelpService } from '../../commonInfra/services/HelpService';
import { Telemetry } from '../../commonInfra/services/Telemetry';

export class SiteIdValidRegExp {
  private static Chars: string = 'A-Za-z0-9\-\_';
  public static InvalidFind: RegExp = new RegExp('[^' + SiteIdValidRegExp.Chars + ']', 'g');
  public static InvalidTest: RegExp = new RegExp('^[' + SiteIdValidRegExp.Chars + ']+$');
}

export interface ISiteSettingsForServerAdminFormScope extends ISettingsCoordinatorCtrlScope {

  siteSettingsForServerAdminForm: ng.IFormController; // from siteSettingsForServerAdminForm.pug

  hideAvailability: boolean;
  hideRevisionHistoryLimitWarning: boolean;
  autoSelectSiteName: boolean;
  active: string;
  suspended: string;
  url: string;
  defaultSubscriptionsEmail: string;
  userCountMax: number;
  tierCreatorCountMax: number;
  tierExplorerCountMax: number;
  tierViewerCountMax: number;
  storageQuotaMax: number;
  versionLimitMin: number;
  versionLimitMax: number;
  isSeatLicensing: boolean;
  isSubscriptionLicensing: boolean;
  server: ServerService;
  unbindNameWatcher: Function;
  clearOrphanedManagedKeychain: () => void;
  clearSavedRevisions: () => void;
  excessRevisionsMessage: string;
  isGuestEnabledOnServer: () => boolean;
  isSessionMultiSite: () => boolean;
  cacheWarmupLearnMoreHref: string;
  showSiteLevelGuestAccessControl: () => boolean;
  isDefaultSite: boolean;
  flowsOnServerEnabled: boolean;
  catalogingOnServerEnabled: boolean;
  catalogingLearnMoreHref: string;
  managedKeychainCountMessage: string;
  calculateManagedKeychainCount: () => void;
  extractRefreshAutoSuspendLearnMoreHref: string;
}

export class SiteSettingsForServerAdminFormCtrl {

  private master: ISiteSettingsForServerAdmin;

  public static $inject = [
    '$scope',
    '$location',
    '$q',
    '$translate',
    'SettingsFormService',
    'ClearOrphanedManagedKeychainAction',
    'ClearSavedRevisionsAction',
    'ServerService',
    'Sites',
    'LicensesService',
  ];

  public static InputDebounceMs: number = 200;

  constructor(
    private scope: ISiteSettingsForServerAdminFormScope,
    private $location: ng.ILocationService,
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
            formService: SettingsFormService,
    private clearOrphanedManagedKeychainAction: ClearOrphanedManagedKeychainAction,
    private clearSavedRevisionsAction: ClearSavedRevisionsAction,
    private server: ServerService,
    private sitesResource: Sites,
            licenses: LicensesService,
  ) {
    if (this.scope.isDefaultSite == null) {
     this.scope.isDefaultSite = this.scope.siteSettingsForServerAdmin.settings
      && this.scope.siteSettingsForServerAdmin.settings.urlName === '';
    }

    this.scope.active = SiteAvailabilityState.ACTIVE;
    this.scope.suspended = SiteAvailabilityState.SUSPENDED;
    this.scope.defaultSubscriptionsEmail = this.server.defaultSubscriptionsEmail();
    licenses.fetch().then((licensingInfo: ILicensingInfoWithTotals) => {
      if (licensingInfo) {
        const noLicenses = !licensingInfo.productKeyInfos || !licensingInfo.productKeyInfos.length;
        if (licensingInfo.coreLicensingInfos || noLicenses) {
          this.scope.userCountMax = NumberInputRange.INT.MAX;
          this.scope.tierExplorerCountMax = NumberInputRange.INT.MAX;
        } else { //Not core so compute the maximum users
          this.scope.userCountMax = licensingInfo.totalExplorerUsers;
          if(licensingInfo.totalCreatorUsers){
            this.scope.userCountMax+=licensingInfo.totalCreatorUsers;
          }
          if(licensingInfo.totalViewerUsers){
            this.scope.userCountMax+=licensingInfo.totalViewerUsers;
          }
          this.scope.tierExplorerCountMax = licensingInfo.totalExplorerUsers ? licensingInfo.totalExplorerUsers : 0;
        }
        this.scope.tierCreatorCountMax = licensingInfo.totalCreatorUsers ? licensingInfo.totalCreatorUsers : 0;
        this.scope.tierViewerCountMax = licensingInfo.totalViewerUsers ? licensingInfo.totalViewerUsers : 0;
      }
    });
    this.scope.storageQuotaMax = NumberInputRange.LONG.MAX / ByteSizeConversionFactor.ONE_GB;
    this.scope.versionLimitMin = 2;
    this.scope.versionLimitMax = 10000;
    this.scope.server = this.server;

    this.scope.isGuestEnabledOnServer = () => this.server.isGuestEnabled();
    this.scope.isSessionMultiSite= () => this.server.isSessionMultiSite();
    this.scope.showSiteLevelGuestAccessControl= () => (!this.scope.isDefaultSite && !this.scope.isSessionMultiSite()) || this.scope.isSessionMultiSite();
    this.scope.cacheWarmupLearnMoreHref = HelpService.getServerHelpUrlByCategory('workbookrefresh');
    this.scope.catalogingLearnMoreHref = HelpService.getServerHelpUrlByCategory('catalog-site-setting');
    this.scope.extractRefreshAutoSuspendLearnMoreHref = HelpService.getServerHelpUrlByCategory('extract-auto-suspend');

    this.initializeUnsetValues();

    this.master = angular.copy(this.scope.siteSettingsForServerAdmin);

    this.scope.sharedForm = formService;
    this.scope.localForm = {
        formController: this.scope.siteSettingsForServerAdminForm,
        save: () => this.callServerToUpdateSiteSettings(),
        cancel: () => this.revertChanges(),
        isBusy: () => this.scope.localForm.busy,
        isFormUnchanged: () => angular.equals(this.master, this.scope.siteSettingsForServerAdmin),
        resetSettings: null
    };

    this.scope.$watch('siteSettingsForServerAdmin.settings.urlName', (value: string) => {
      scope.url = this.$location.absUrl().replace(this.$location.url(), '') +
        SiteSwitchService.getSitePathFragment(value);
    });

    this.scope.$watch('siteSettingsForServerAdmin.settings', (newValue: any) => {
      this.scope.$broadcast('settings.change', newValue);
    }, true);

    this.scope.$watch('siteSettingsForServerAdmin.availability', (newValue: string) => {
      this.scope.$broadcast('availability.change', newValue);
    });

    this.scope.$watch('siteSettingsForServerAdmin.settings.managedKeychainCountEnabled', () => {
       this.calculateManagedKeychainCount();
    });

    this.scope.clearOrphanedManagedKeychain = () => this.clearOrphanedManagedKeychain();

    this.scope.clearSavedRevisions = () => this.clearSavedRevisions(this.scope.siteId);

    if (!this.scope.hideRevisionHistoryLimitWarning) {
      // Async call to get the data for the Revision History Limit warning message, e.g. "Setting the limit to this will delete X revisions"
      const debouncedUpdate = _.debounce((newValue: number) => {
        this.scope.$evalAsync(() => {
          this.setExcessRevisionsMessage(newValue);
        });
      }, SiteSettingsForServerAdminFormCtrl.InputDebounceMs);

      // Kick off debounced update when contentVersionLimit changes
      this.scope.$watch('siteSettingsForServerAdmin.settings.contentVersionLimit', (value: number) => {
        this.scope.excessRevisionsMessage = '';
        if (_.isNumber(value)) {
          debouncedUpdate(value);
        }
      });
    }
  }

  private initializeUnsetValues(){
        // Initialize unset values so angular doesn't overwrite them with empty string
    if (this.scope.siteSettingsForServerAdmin && this.scope.siteSettingsForServerAdmin.settings) {
      if (this.scope.siteSettingsForServerAdmin.settings.userQuotaMode === UserQuotaMode.SERVER_LIMIT) {
        this.scope.siteSettingsForServerAdmin.settings.userQuota = null;
        this.scope.siteSettingsForServerAdmin.settings.tierCreatorCapacity = null;
        this.scope.siteSettingsForServerAdmin.settings.tierExplorerCapacity = null;
        this.scope.siteSettingsForServerAdmin.settings.tierViewerCapacity = null;
      }
      if (this.scope.siteSettingsForServerAdmin.settings.userQuotaMode === UserQuotaMode.SITE_TIERED_CAPACITY) {
        this.scope.siteSettingsForServerAdmin.settings.userQuota = null;
      }
      if (this.scope.siteSettingsForServerAdmin.settings.userQuotaMode === UserQuotaMode.SITE_USER_QUOTA) {
        this.scope.siteSettingsForServerAdmin.settings.tierCreatorCapacity = null;
        this.scope.siteSettingsForServerAdmin.settings.tierExplorerCapacity = null;
        this.scope.siteSettingsForServerAdmin.settings.tierViewerCapacity = null;
      }
      if (!this.scope.siteSettingsForServerAdmin.settings.storageQuotaEnabled) {
        this.scope.siteSettingsForServerAdmin.settings.storageQuotaBytes = null;
      }
      if (!this.scope.siteSettingsForServerAdmin.settings.customSubscriptionEmailEnabled) {
        this.scope.siteSettingsForServerAdmin.settings.customSubscriptionEmail = angular.copy(this.scope.defaultSubscriptionsEmail);
      }
      if (!(this.scope.siteSettingsForServerAdmin.settings.contentVersionLimit >= 0)) {
        this.scope.siteSettingsForServerAdmin.settings.contentVersionLimit = null;
      }
      // Copy a scrubbed version of the inputted name into the ID field until the ID is edited
      if (this.scope.siteSettingsForServerAdmin.settings.urlName === '' && !this.scope.isDefaultSite) {
        this.scope.unbindNameWatcher = this.scope.$watch('siteSettingsForServerAdmin.settings.name', (value: string) => {
          this.scope.siteSettingsForServerAdmin.settings.urlName = _.isString(value) ? value.replace(SiteIdValidRegExp.InvalidFind, '') : '';
        });
      }
    }
    this.scope.flowsOnServerEnabled = this.server.flowsOnServerEnabled();
    this.scope.catalogingOnServerEnabled = this.server.catalogingOnServerEnabled();
  }

  private revertChanges(): void {
    this.scope.siteSettingsForServerAdmin = angular.copy(this.master);
    // TODO: Use Angular 1.3 $validators/$validate and pull validation out of $parsers/$formatters
    _.each(this.scope.siteSettingsForServerAdminForm.$error, (error: ng.INgModelController[], key: string) => {
      if (error) {
        error[0].$setValidity(key, true);
      }
    });
  }

  // Used in edit site modal
  public save(): void {
    this.callServerToUpdateSiteSettings();
  }

  public cleanupUserQuotaValues(): void {
    const mode = this.scope.siteSettingsForServerAdmin.settings.userQuotaMode;
    if (mode === UserQuotaMode.SERVER_LIMIT) {
      this.scope.siteSettingsForServerAdmin.settings = {
        ...this.scope.siteSettingsForServerAdmin.settings,
        tierCreatorCapacity: null,
        tierExplorerCapacity: null,
        tierViewerCapacity: null,
        userQuota: null,
      };
    }
    if (mode === UserQuotaMode.SITE_USER_QUOTA) {
      this.scope.siteSettingsForServerAdmin.settings = {
        ...this.scope.siteSettingsForServerAdmin.settings,
        tierCreatorCapacity: null,
        tierExplorerCapacity: null,
        tierViewerCapacity: null,
      };
    }
    if (mode === UserQuotaMode.SITE_TIERED_CAPACITY) {
      this.scope.siteSettingsForServerAdmin.settings = {
        ...this.scope.siteSettingsForServerAdmin.settings,
        userQuota: null,
      };
    }
  }

  private callServerToUpdateSiteSettings(): ng.IPromise<any> {
    const updatePromises: ng.IPromise<any>[] = [];
    this.cleanupUserQuotaValues();
    const site = angular.copy(this.scope.siteSettingsForServerAdmin);

    this.scope.localForm.busy,
    updatePromises.push(this.sitesResource.updateSiteSettingsForServerAdmin(
      site.id,
      site.settings
      ));

    if (site.availability !== this.master.availability) {
      if (site.availability === SiteAvailabilityState.ACTIVE) {
        updatePromises.push(this.sitesResource.activate([site.id]));
      } else {
        updatePromises.push(this.sitesResource.suspend([site.id]));
      }
    }

    // todo use ResultHelper.combineResults here instead of duplicating the logic
    return this.$q.all(updatePromises)
      .then((results: IResult[]) => {
        let errors: IError[] = [];

        angular.forEach(results, (result: IResult) => {
          if (result.errors && result.errors.length) {
            errors = errors.concat(result.errors);
          }
        });

        if (errors.length) {
          this.scope.sharedForm.state.updateFailure(errors, site);
          return this.$q.reject();
        } else {
          this.updateSuccess(site);
        }
      })
      .finally(() => this.scope.localForm.busy = false);
  }

  private clearOrphanedManagedKeychain(){
    this.clearOrphanedManagedKeychainAction.confirmDeleteOrphanedManagedKeychain();
    this.scope.siteSettingsForServerAdmin.settings.managedKeychainCountEnabled = false;
  }

  private clearSavedRevisions(siteId: string) {
    this.clearSavedRevisionsAction.confirmDeleteSiteRevisions(siteId);
  }

  // Success/Error Handlers

  private updateSuccess(site: ISiteSettingsForServerAdmin): void {
    // Only need to update current view and state if we're in server or site settings
    if (!NavLocationSelectors.inSettingsLocation(appStore.getState())) {
      return;
    }

    // send site setting for server admin telemetry events
    Telemetry.sendEvent<SiteSettingServerAdminTelemetryEvent>(buildSiteSettingServerAdminTelemetryEvent(site, this.master,  getSiteSettingUUID(false)));

    // Trigger update of session info state as settings update may have changed it.
    this.server.refreshSessionInfo();
    // Refresh session info when changing a setting that affects it
    if (site.settings.name !== this.master.settings.name ||
        site.settings.catalogingOnSiteEnabled !== this.master.settings.catalogingOnSiteEnabled ||
        site.settings.flowsOnSiteEnabled !== this.master.settings.flowsOnSiteEnabled ||
        site.settings.authoringEnabled !== this.master.settings.authoringEnabled ||
        site.settings.webEditingEnabled !== this.master.settings.webEditingEnabled) {
      this.server.refreshSessionInfo(); // Update site picker and ServerService
    }

    // Changed site url name in site settings page
    if (site.settings.urlName !== '' && site.settings.urlName !== this.master.settings.urlName) {
      const urls = new SpacesSiteUrls({siteUrlName: site.settings.urlName});
      Location.instance.setUrl(urls.settingsUrl());
    }

    this.master = angular.copy(site);
  }

  private calculateManagedKeychainCount(): void {
    if (!this.scope.siteSettingsForServerAdmin.settings.managedKeychainCountEnabled){
      return;
    }
    this.scope.managedKeychainCountMessage = '';
    const debouncedUpdate = _.debounce(() => {
      this.scope.$evalAsync(() => {
        this.setManagedKeychainCountMessage();
      });
    }, SiteSettingsForServerAdminFormCtrl.InputDebounceMs);
    debouncedUpdate();
  }

  private setManagedKeychainCountMessage(): void {
    this.server.sendRequest(new GetNumManagedKeychainsRequest({}))
      .then((result: IGetNumManagedKeychainResult) => {
        const orphanedManagedKeychainCount = result.orphanedManagedKeychainCount;
        const totalManagedKeychainCount = result.totalManagedKeychainCount;
        this.scope.managedKeychainCountMessage = this.$translate.instant('managedKeychainCountValuesMsg',
        {
          TOTAL_COUNT: L10n.localizedCount(totalManagedKeychainCount),
          ORPHANED_COUNT: L10n.localizedCount(orphanedManagedKeychainCount)});
        }
      );
  }

  private setExcessRevisionsMessage(input: number): void {
    const params: IGetNumExcessRevisionsParams = {
      limit: input
    };

    // This code contains a bug, because the getNumExcessRevisions request is implicitly site-scoped,
    // but this UI can show up in the edit site settings dialog from the all sites list.
    // The effect is that we'll fetch the excess revisions info for whatever site the
    // server admin is signed into regardless of which site they're editing.
    // TFS: 815654
    this.server.sendRequest(new GetNumExcessRevisionsRequest(params))
      .then((result: IGetNumExcessRevisionsResult) => {
        if (input === this.scope.siteSettingsForServerAdmin.settings.contentVersionLimit) {
          const workbookRevisionCount = result.workbookRevisionCount;
          const datasourceRevisionCount = result.datasourceRevisionCount;
          const flowRevisionCount = result.flowRevisionCount;

          if (this.master.settings.flowsOnSiteEnabled &&
             (flowRevisionCount > 0 || workbookRevisionCount > 0 || datasourceRevisionCount > 0)) {
            this.scope.excessRevisionsMessage = this.$translate.instant('contentVersionLimitWarningWithWorkbookDataSourceAndFlowValues',
              { LIMIT_COUNT: L10n.localizedCount(input),
                EXCESS_WORKBOOK_COUNT: L10n.localizedCount(workbookRevisionCount),
                EXCESS_DATASOURCE_COUNT: L10n.localizedCount(datasourceRevisionCount),
                EXCESS_FLOW_COUNT: L10n.localizedCount(flowRevisionCount)});
          } else if (workbookRevisionCount > 0 || datasourceRevisionCount > 0) {
            this.scope.excessRevisionsMessage = this.$translate.instant('contentVersionLimitWarningWithWorkbookAndDataSourceValues',
              { LIMIT_COUNT: L10n.localizedCount(input),
                EXCESS_WORKBOOK_COUNT: L10n.localizedCount(workbookRevisionCount),
                EXCESS_DATASOURCE_COUNT: L10n.localizedCount(datasourceRevisionCount)});
          }
        }
      }
    );
  }

}

Module.instance().controller('SiteSettingsForServerAdminFormCtrl', SiteSettingsForServerAdminFormCtrl);
